import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchTemplates, fetchTemplateMessage, previewTemplate, createTemplate, updateTemplate, deleteTemplate
} from './attendanceApi';

import ReportImagePanel from './ReportImagePanel';
import { copyText } from '../../utils/copyText';

const NEW = '__new__';
const field = { width: '100%', padding: 8, borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };
const green = { border: 'none', borderRadius: 10, background: '#25d366', color: '#fff', fontWeight: 800, cursor: 'pointer' };
const small = { border: 'none', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', background: '#e5e7eb' };

// Modal shown after attendance is saved.
// Dropdown (4 system + custom + "Create New Template") -> editable textarea -> Send to WhatsApp.
export default function WhatsAppTemplateSender({ user, className, date, onClose }) {
  const [tab, setTab] = useState('text');          // 'text' | 'image'
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState([]);
  const [selected, setSelected] = useState('');
  const [text, setText] = useState('');            // final message (send mode)
  const [groupLink, setGroupLink] = useState('');
  const [builder, setBuilder] = useState(null);    // { id|null, name, body } in create/edit mode
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);

  const err = (e, fallback) => setNote(e.response?.data?.error || fallback);

  const reload = useCallback(async () => {
    const r = await fetchTemplates(user);
    setTemplates(r.templates); setVariables(r.variables);
  }, [user]);

  useEffect(() => {
    reload().catch((e) => setNote(e.response?.data?.error || 'Could not load templates'));
  }, [reload]);

  const onSelect = async (id) => {
    setSelected(id); setNote(''); setText(''); setBuilder(null);
    if (!id) return;
    if (id === NEW) { setBuilder({ id: null, name: '', body: '' }); return; }
    setBusy(true);
    try {
      const r = await fetchTemplateMessage(user, className, date, id);
      setText(r.message); setGroupLink(r.groupLink);
    } catch (e) { err(e, 'Could not build message'); } finally { setBusy(false); }
  };

  // Insert a {{VARIABLE}} exactly at the cursor (or over the current selection).
  const insertVar = (v) => {
    const el = bodyRef.current;
    const tag = `{{${v}}}`;
    const start = el ? el.selectionStart : builder.body.length;
    const end = el ? el.selectionEnd : builder.body.length;
    setBuilder({ ...builder, body: builder.body.slice(0, start) + tag + builder.body.slice(end) });
    requestAnimationFrame(() => { // put the caret right after the inserted tag
      if (!bodyRef.current) return;
      bodyRef.current.focus();
      bodyRef.current.setSelectionRange(start + tag.length, start + tag.length);
    });
  };

  const preview = async () => {
    setBusy(true); setNote('');
    try { const r = await previewTemplate(user, className, date, builder.body); setNote(`Preview:\n${r.message}`); }
    catch (e) { err(e, 'Preview failed'); } finally { setBusy(false); }
  };

  const saveBuilder = async () => {
    setBusy(true); setNote('');
    try {
      const saved = builder.id
        ? await updateTemplate(user, builder.id, builder.name, builder.body)
        : await createTemplate(user, builder.name, builder.body);
      await reload();
      await onSelect(saved.id); // jump straight to the generated message
    } catch (e) { err(e, 'Save failed'); setBusy(false); }
  };

  const removeCurrent = async () => {
    if (!window.confirm('Delete this custom template?')) return;
    try { await deleteTemplate(user, selected); await reload(); setSelected(''); setText(''); setNote('Template deleted'); }
    catch (e) { err(e, 'Delete failed'); }
  };

  const current = templates.find((t) => t.id === selected);

  // Native share sheet (Android). No wa.me / chat.whatsapp.com links: they cannot pre-fill text into groups.
  // imageFile (optional): a PNG File to attach alongside the text.
  const sendToWhatsApp = async (imageFile) => {
    const file = imageFile instanceof File ? imageFile : null;
    const data = { title: 'Attendance Report', text }; // final, possibly hand-edited text
    if (file) data.files = [file];

    const fallbackCopy = async (reason) => {
      const copied = await copyText(text);
      const msg = copied
        ? `${reason} Message copied to clipboard. Open WhatsApp and paste it in the group.`
        : `${reason} Copy failed. Select the text above and copy it manually.`;
      setNote(msg);
      window.alert(msg);
    };

    if (typeof navigator.share !== 'function') {
      await fallbackCopy('Sharing is not supported on this browser.');
      return;
    }

    // If files can't be shared on this device, share text only.
    if (data.files && !(navigator.canShare && navigator.canShare({ files: data.files }))) delete data.files;

    try {
      await navigator.share(data);
      setNote('Choose WhatsApp (or any app) in the share sheet and select the group.');
    } catch (e) {
      if (e && e.name === 'AbortError') { setNote('Share cancelled.'); return; } // user closed the sheet
      await fallbackCopy('Could not open the share sheet.');
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 9500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 16, width: '100%', maxWidth: 560, maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>📲 WhatsApp Report – {className} · {date}</h3>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 22, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ display: 'flex', gap: 6, margin: '10px 0' }}>
          {[['text', '💬 Text Message'], ['image', '🖼 Generate Image']].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              style={{ ...small, flex: 1, padding: 9, fontWeight: 700, background: tab === k ? '#2563eb' : '#e5e7eb', color: tab === k ? '#fff' : '#111827' }}>{label}</button>
          ))}
        </div>

        {<select value={selected} onChange={(e) => onSelect(e.target.value)} style={{ ...field, margin: '10px 0' }}>
          <option value="">-- Select WhatsApp Template --</option>
          <optgroup label="System templates">
            {templates.filter((t) => t.system).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </optgroup>
          {templates.some((t) => !t.system) && (
            <optgroup label="My custom templates">
              {templates.filter((t) => !t.system).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </optgroup>
          )}
          <option value={NEW}>➕ Create New Template</option>
        </select>}

        {current && !current.system && !builder && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <button style={small} onClick={() => setBuilder({ id: current.id, name: current.name, body: current.body })}>✏️ Edit template</button>
            <button style={{ ...small, background: '#fee2e2' }} onClick={removeCurrent}>🗑 Delete template</button>
          </div>
        )}

        {tab === 'image' && selected && selected !== NEW && !builder && <ReportImagePanel user={user} className={className} date={date} templateId={selected} />}

        {tab === 'text' && busy && <div style={{ fontSize: 13 }}>Working…</div>}

        {builder && (
          <div>
            <input placeholder="Template name" maxLength={60} value={builder.name}
              onChange={(e) => setBuilder({ ...builder, name: e.target.value })} style={{ ...field, marginBottom: 8 }} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
              {variables.map((v) => (
                <button key={v} type="button"
                  onMouseDown={(e) => e.preventDefault() /* keep the caret in the textarea */}
                  onClick={() => insertVar(v)}
                  style={{ ...small, background: '#dcfce7', fontSize: 11 }}>{`{{${v}}}`}</button>
              ))}
            </div>
            <textarea ref={bodyRef} rows={10} maxLength={3000} value={builder.body}
              placeholder="Type your message, then click a variable chip to insert it at the cursor…"
              onChange={(e) => setBuilder({ ...builder, body: e.target.value })} style={field} />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button style={small} onClick={preview} disabled={!builder.body.trim()}>👁 Preview</button>
              <button style={{ ...green, padding: '8px 14px' }} onClick={saveBuilder}
                disabled={!builder.name.trim() || !builder.body.trim()}>💾 Save template</button>
            </div>
          </div>
        )}

        {tab === 'text' && text && !builder && (
          <>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={13} style={field} />
            <button onClick={() => sendToWhatsApp()} style={{ ...green, marginTop: 8, width: '100%', padding: 12, fontSize: 15 }}>
              📲 Send to WhatsApp
            </button>
            {current && current.system && (
              <button style={{ ...small, marginTop: 8 }}
                onClick={() => setBuilder({ id: null, name: `${current.name} (copy)`.slice(0, 60), body: current.body })}>
                📄 Save as custom copy
              </button>
            )}
          </>
        )}
        {tab === 'text' && note && <div style={{ marginTop: 8, fontSize: 12, color: '#166534', whiteSpace: 'pre-wrap' }}>{note}</div>}
      </div>
    </div>
  );
}
